import { CommonModule } from '@angular/common';
import {
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  inject,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

export interface ManageGeneratorAutocompleteSuggestion {
  id?: number | string | null;
  name?: string | null;
}

export interface ManageGeneratorAutocompleteCompleteEvent {
  originalEvent: Event | null;
  query: string;
}

export type ManageGeneratorAutocompleteSize = 'default' | 'compact';

@Component({
  selector: 'p-autocomplete',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './manage-generator-autocomplete.html',
  styleUrl: './manage-generator-autocomplete.scss',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => ManageGeneratorAutocompleteComponent),
      multi: true,
    },
  ],
})
export class ManageGeneratorAutocompleteComponent implements ControlValueAccessor, OnChanges, OnDestroy {
  @Input() suggestions: ManageGeneratorAutocompleteSuggestion[] = [];
  @Input() dropdown = false;
  @Input() placeholder = '';
  @Input() disabled = false;
  @Input() optionLabel = 'name';
  @Input() inputId = '';
  @Input() size: ManageGeneratorAutocompleteSize = 'default';
  @Input() showSuggestionCheckbox = false;
  @Input() selectedSuggestionIds: Array<number | string> = [];
  @Input() selectedSuggestions: ManageGeneratorAutocompleteSuggestion[] = [];

  @Output() completeMethod = new EventEmitter<ManageGeneratorAutocompleteCompleteEvent>();
  @Output() fieldBlur = new EventEmitter<FocusEvent>();

  protected inputValue = '';
  protected panelOpen = false;

  private readonly hostElement = inject<ElementRef<HTMLElement>>(ElementRef);
  private onChange: (value: string) => void = () => undefined;
  private onTouched: () => void = () => undefined;

  writeValue(value: string | null): void {
    this.inputValue = value ?? '';
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['suggestions'] && this.panelOpen && !this.suggestions.length) {
      this.panelOpen = false;
    }
  }

  ngOnDestroy(): void {
    this.panelOpen = false;
  }

  protected handleInput(event: Event): void {
    const input = event.target as HTMLInputElement | null;
    this.inputValue = input?.value ?? '';
    this.onChange(this.inputValue);
    this.panelOpen = true;
    this.completeMethod.emit({ originalEvent: event, query: this.inputValue });
  }

  protected handleFocus(event: FocusEvent): void {
    if (this.disabled) {
      return;
    }

    this.panelOpen = true;
    this.completeMethod.emit({ originalEvent: event, query: this.inputValue });
  }

  protected handleBlur(event: FocusEvent): void {
    globalThis.setTimeout(() => {
      this.panelOpen = false;
      this.onTouched();
      this.fieldBlur.emit(event);
    }, 120);
  }

  protected handleDropdownClick(event: MouseEvent): void {
    if (this.disabled) {
      return;
    }

    event.preventDefault();
    this.panelOpen = true;
    this.completeMethod.emit({ originalEvent: event, query: this.inputValue });
  }

  protected handlePanelMouseDown(event: MouseEvent): void {
    event.preventDefault();
  }

  protected selectSuggestion(suggestion: ManageGeneratorAutocompleteSuggestion): void {
    const label = this.getSuggestionLabel(suggestion);
    if (this.showSuggestionCheckbox) {
      this.inputValue = '';
      this.onChange('');
      this.completeMethod.emit({ originalEvent: null, query: label });
      this.panelOpen = true;
      return;
    }
    this.inputValue = label;
    this.onChange(label);
    this.panelOpen = false;
    this.completeMethod.emit({ originalEvent: null, query: label });
  }

  protected isSuggestionSelected(suggestion: ManageGeneratorAutocompleteSuggestion): boolean {
    return this.selectedSuggestionIds.includes(suggestion.id ?? '');
  }

  protected getVisibleSelectedSuggestions(): ManageGeneratorAutocompleteSuggestion[] {
    return this.selectedSuggestions.slice(0, 2);
  }

  protected getHiddenSelectedSuggestionCount(): number {
    return Math.max(0, this.selectedSuggestions.length - 2);
  }

  protected getSuggestionLabel(suggestion: ManageGeneratorAutocompleteSuggestion): string {
    const label = suggestion?.[this.optionLabel as keyof ManageGeneratorAutocompleteSuggestion];
    return typeof label === 'string' ? label : '';
  }

  protected trackBySuggestion(index: number, suggestion: ManageGeneratorAutocompleteSuggestion): string | number {
    return suggestion.id ?? this.getSuggestionLabel(suggestion) ?? index;
  }

  @HostListener('document:mousedown', ['$event'])
  protected handleDocumentClick(event: MouseEvent): void {
    const host = this.hostElement.nativeElement;
    if (!host.contains(event.target as Node)) {
      this.panelOpen = false;
    }
  }
}
